import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import {
  Trash2, Plus, Save, Landmark, Wallet, Coins, PiggyBank, CreditCard,
  ChevronDown, ChevronRight, Receipt, AlertTriangle,
} from "lucide-react";

// Human-friendly type labels + icons + whether they count as available cash
// by default. Credit cards are intentionally NOT here — they live in Debts.
const TYPE_META = {
  cash:     { label: "Cash on hand", icon: Coins,      affectsCash: true  },
  checking: { label: "Checking",     icon: Landmark,   affectsCash: true  },
  savings:  { label: "Savings",      icon: PiggyBank,  affectsCash: true  },
  pay_card: { label: "Paycheck card", icon: CreditCard, affectsCash: true },
  other:    { label: "Other",        icon: Wallet,     affectsCash: false },
};

export default function Accounts() {
  const [searchParams] = useSearchParams();
  const showEarmarkBanner = searchParams.get("nudge") === "earmark";
  const [sources, setSources] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSource, setNewSource] = useState({
    name: "", type: "checking",
    account_category: "general_spending",
    contributes_to_total_balance: true,
    current_source_balance_optional: "",
  });

  const load = async () => {
    setLoading(true);
    const [fs, b] = await Promise.all([
      api.get("/funding-sources"),
      api.get("/bills"),
    ]);
    setSources(fs.data);
    setBills(b.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Bills grouped by funding source. For each source we keep only the
  // "next upcoming unpaid or pending" instance per recurring chain so users
  // see one row per bill (not 14 monthly duplicates). Non-recurring bills
  // are always included if still unpaid or pending.
  const billsBySource = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    const map = {}; // id -> array of bills
    const seenRecurring = {}; // "sourceId|billName" -> true (first upcoming wins)
    const sorted = [...bills]
      .filter((b) => b.status === "unpaid" || b.status === "pending")
      .filter((b) => (b.due_date || "") >= todayIso.slice(0, 7)) // include current month onward
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    for (const b of sorted) {
      const key = b.funding_source_id || "__unassigned__";
      if (b.is_recurring) {
        const k = `${key}|${(b.name || "").toLowerCase()}`;
        if (seenRecurring[k]) continue;
        seenRecurring[k] = true;
      }
      (map[key] = map[key] || []).push(b);
    }
    return map;
  }, [bills]);

  const unassignedBills = billsBySource["__unassigned__"] || [];

  const cashSources = sources.filter((s) => s.contributes_to_total_balance);
  const creditSources = sources.filter((s) => !s.contributes_to_total_balance);
  const totalCash = cashSources.reduce(
    (s, f) => s + (f.current_source_balance_optional == null ? 0 : Number(f.current_source_balance_optional)),
    0,
  );
  const totalCredit = creditSources.reduce(
    (s, f) => s + (f.current_source_balance_optional == null ? 0 : Number(f.current_source_balance_optional)),
    0,
  );

  const addSource = async (e) => {
    e.preventDefault();
    if (!newSource.name.trim()) return;
    try {
      const payload = {
        name: newSource.name.trim(),
        type: newSource.type,
        account_category: newSource.account_category || "general_spending",
        contributes_to_total_balance: !!newSource.contributes_to_total_balance,
        current_source_balance_optional: newSource.current_source_balance_optional === ""
          ? null : Number(newSource.current_source_balance_optional),
      };
      const { data } = await api.post("/funding-sources", payload);
      setSources((prev) => [...prev, data]);
      setNewSource({ name: "", type: "checking", account_category: "general_spending", contributes_to_total_balance: true, current_source_balance_optional: "" });
      setShowAddForm(false);
      toast.success(`Added ${data.name}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const patchSource = async (id, patch) => {
    try {
      const { data } = await api.patch(`/funding-sources/${id}`, patch);
      setSources((prev) => prev.map((s) => (s.id === id ? { ...s, ...data } : s)));
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const delSource = async (source) => {
    const backup = { ...source };
    setSources((prev) => prev.filter((s) => s.id !== source.id));
    try {
      await api.delete(`/funding-sources/${source.id}`);
      toast(`Deleted "${source.name}"`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post("/funding-sources", {
                name: backup.name, type: backup.type,
                contributes_to_total_balance: !!backup.contributes_to_total_balance,
                current_source_balance_optional: backup.current_source_balance_optional ?? null,
                notes: backup.notes || null,
              });
              setSources((prev) => [...prev, data]);
              toast.success("Restored");
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (err) {
      setSources((prev) => [...prev, backup]);
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Money accounts</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Where your money lives</h1>
        <p className="text-[#59605C] mt-1 max-w-2xl">
          Track every bank account, savings, cash on hand, and paycheck card. Your total available cash is <span className="text-[#2C302E] font-medium">calculated automatically</span> — just keep each balance up to date and the whole forecast follows.
          <br />
          <span className="text-xs text-[#8A918D]">Credit cards are tracked separately under <span className="font-medium text-[#2C302E]">Debts</span> — they represent money you owe, not cash you can spend.</span>
        </p>
        <button className="btn-primary mt-4" onClick={() => setShowAddForm(true)} data-testid="accounts-add-button">
          <Plus size={16} /> Add account
        </button>
      </div>

      {/* Contextual banner when arriving from the Dashboard's "Adjust
          accounts →" nudge. Explains what to do in this page so the user
          doesn't have to remember why they clicked over. */}
      {showEarmarkBanner && (
        <div
          className="card-soft p-5 bg-[#FBEEEA] border-2 border-[#E5B4A3] flex items-start gap-3"
          data-testid="accounts-earmark-banner"
        >
          <span className="w-9 h-9 rounded-full bg-[#F5D9D1] text-[#7A2E24] flex items-center justify-center shrink-0" aria-hidden="true">
            <AlertTriangle size={18} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg font-semibold text-[#7A2E24]">Move money to your Bills &amp; Planned account</div>
            <p className="text-sm text-[#59605C] mt-1 leading-relaxed">
              Your Bills &amp; Planned balance doesn&rsquo;t cover your upcoming bills. To fix it: <strong>edit the source account</strong> (Savings or General Spending) to lower its balance by the amount you&rsquo;re moving, then <strong>edit the Bills &amp; Planned account</strong> and raise its balance by the same amount. This is a book-keeping change — do the actual transfer in your bank app.
            </p>
          </div>
        </div>
      )}

      {/* Auto-calculated summary */}
      <div className="grid gap-4">
        <div className="card-soft p-5 bg-[#EEF3EC] border-[#D5E1D3]" data-testid="accounts-cash-summary">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#3B5B3E]">Total available cash</div>
          <div className="font-heading text-3xl font-semibold text-[#3B5B3E] mt-2" data-testid="accounts-total-cash">
            {formatMoney(totalCash)}
          </div>
          <div className="text-xs text-[#59605C] mt-1">
            Auto-calculated from {cashSources.length} account{cashSources.length === 1 ? "" : "s"}.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#59605C]">Loading…</div>
      ) : (
        <>
          {/* Cash accounts */}
          <section data-testid="accounts-cash-section">
            <h2 className="font-heading text-lg font-medium text-[#2C302E] mb-3">Cash accounts</h2>
            {cashSources.length === 0 ? (
              <div className="card-soft p-6 text-sm text-[#59605C] italic">
                Nothing here yet. Add a checking, savings, or cash account to see it in the dashboard.
              </div>
            ) : (
              <div className="space-y-2">
                {cashSources.map((s) => (
                  <FundingSourceRow key={s.id} source={s} bills={billsBySource[s.id] || []} onPatch={patchSource} onDelete={() => delSource(s)} />
                ))}
              </div>
            )}
          </section>

          {/* Non-cash / other (e.g. paycheck cards user un-flagged from cash) */}
          {creditSources.length > 0 && (
            <section data-testid="accounts-credit-section">
              <h2 className="font-heading text-lg font-medium text-[#2C302E] mb-3">Not counted as cash</h2>
              <div className="space-y-2">
                {creditSources.map((s) => (
                  <FundingSourceRow key={s.id} source={s} bills={billsBySource[s.id] || []} onPatch={patchSource} onDelete={() => delSource(s)} />
                ))}
              </div>
            </section>
          )}

          {/* Bills not assigned to any account — surface these so the user
              can tie them to a funding source. */}
          {unassignedBills.length > 0 && (
            <section data-testid="accounts-unassigned-section">
              <UnassignedBillsCard bills={unassignedBills} />
            </section>
          )}
        </>
      )}

      {showAddForm && (
        <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={() => setShowAddForm(false)}>
          <form className="card-soft p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} onSubmit={addSource} data-testid="accounts-add-form">
            <h2 className="font-heading text-xl font-medium mb-4">Add a new account</h2>
            <p className="text-sm text-[#59605C] mb-4">
              Just the name and current balance — this counts toward your total available cash.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-[#59605C]">Account name</label>
                <input className="input-soft" placeholder="e.g. Chase 2672, Wallet Cash" required
                  value={newSource.name} onChange={(e) => setNewSource({...newSource, name: e.target.value})}
                  autoFocus data-testid="accounts-add-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-[#59605C]">Type</label>
                <select className="input-soft" value={newSource.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setNewSource((prev) => ({
                      ...prev, type: t,
                      contributes_to_total_balance: TYPE_META[t]?.affectsCash ?? prev.contributes_to_total_balance,
                    }));
                  }}
                  data-testid="accounts-add-type">
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <option key={k} value={k}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-[#59605C]">Category</label>
                <select
                  className="input-soft"
                  value={newSource.account_category}
                  onChange={(e) => setNewSource({ ...newSource, account_category: e.target.value })}
                  data-testid="accounts-add-category"
                >
                  <option value="bills_planned">Bills &amp; Planned</option>
                  <option value="general_spending">General Spending</option>
                  <option value="savings">Savings</option>
                  <option value="other">Other</option>
                </select>
                <div className="mt-1 text-[10px] text-[#8A918D]">
                  How this account is used. Savings usually shouldn&rsquo;t count as everyday spendable cash.
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-[#59605C]">Current balance</label>
                <input type="number" step="0.01" className="input-soft" placeholder="0.00" required
                  value={newSource.current_source_balance_optional}
                  onChange={(e) => setNewSource({...newSource, current_source_balance_optional: e.target.value})}
                  data-testid="accounts-add-balance" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="accounts-add-submit">Add account</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Bill list row (compact) ----------
function BillLine({ bill }) {
  const due = bill.due_date ? new Date(bill.due_date + "T00:00:00") : null;
  const dueLabel = due
    ? due.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "—";
  const amt = Number(bill.estimated_due_amount || 0);
  return (
    <div className="flex items-center justify-between gap-3 text-sm py-1.5" data-testid={`account-bill-${bill.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Receipt size={12} className="text-[#8A918D] shrink-0" />
        <span className="text-[#2C302E] truncate">{bill.name}</span>
        {bill.is_recurring && (
          <span className="text-[10px] uppercase tracking-widest text-[#8A918D]">recur</span>
        )}
        {bill.is_autopay && (
          <span className="text-[10px] uppercase tracking-widest text-[#3B5B3E]">autopay</span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-[11px] text-[#8A918D]">next {dueLabel}</span>
        <span className="font-heading font-medium text-[#2C302E]">{formatMoney(amt)}</span>
      </div>
    </div>
  );
}

// ---------- "Bills without an account" card ----------
function UnassignedBillsCard({ bills }) {
  const [open, setOpen] = useState(true);
  const total = bills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  return (
    <div className="rounded-2xl border border-dashed border-[#E4C9C2] bg-[#FBF7F0] p-4" data-testid="accounts-unassigned-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="accounts-unassigned-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div>
            <div className="font-heading font-semibold text-[#8C5D30]">Bills without an account</div>
            <div className="text-xs text-[#59605C] mt-0.5">
              {bills.length} bill{bills.length === 1 ? "" : "s"} · {formatMoney(total)} upcoming. Assign an account so the forecast can subtract them from the right balance.
            </div>
          </div>
        </div>
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-[#E4C9C2]/60 divide-y divide-[#EBE6E0]" data-testid="accounts-unassigned-list">
          {bills.map((b) => <BillLine key={b.id} bill={b} />)}
        </div>
      )}
    </div>
  );
}

// ---------- Inline-editable funding source row ----------
function FundingSourceRow({ source, bills = [], onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    name: source.name,
    type: source.type || "checking",
    account_category: source.account_category || "general_spending",
    contributes_to_total_balance: !!source.contributes_to_total_balance,
    current_source_balance_optional: source.current_source_balance_optional ?? "",
  });
  const [saving, setSaving] = useState(false);

  const meta = TYPE_META[source.type] || TYPE_META.checking;
  const Icon = meta.icon;
  const totalOutflow = bills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);

  useEffect(() => {
    setForm({
      name: source.name, type: source.type || "checking",
      account_category: source.account_category || "general_spending",
      contributes_to_total_balance: !!source.contributes_to_total_balance,
      current_source_balance_optional: source.current_source_balance_optional ?? "",
    });
  }, [source]);

  const save = async () => {
    setSaving(true);
    try {
      await onPatch(source.id, {
        name: form.name.trim(),
        type: form.type,
        account_category: form.account_category,
        contributes_to_total_balance: !!form.contributes_to_total_balance,
        current_source_balance_optional: form.current_source_balance_optional === ""
          ? null : Number(form.current_source_balance_optional),
      });
      toast.success(`Updated ${form.name}`);
      setEditing(false);
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="p-4 rounded-2xl bg-[#F9F8F6] border border-[#4A6148]/30" data-testid={`fs-edit-${source.id}`}>
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Name</label>
            <input className="input-soft" autoFocus value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              data-testid={`fs-edit-name-${source.id}`} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Type</label>
            <select className="input-soft" value={form.type}
              onChange={(e) => {
                const t = e.target.value;
                setForm((prev) => ({
                  ...prev, type: t,
                  contributes_to_total_balance: TYPE_META[t]?.affectsCash ?? prev.contributes_to_total_balance,
                }));
              }}
              data-testid={`fs-edit-type-${source.id}`}>
              {Object.entries(TYPE_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Category</label>
            <select
              className="input-soft"
              value={form.account_category}
              onChange={(e) => setForm({ ...form, account_category: e.target.value })}
              data-testid={`fs-edit-category-${source.id}`}
            >
              <option value="bills_planned">Bills &amp; Planned</option>
              <option value="general_spending">General Spending</option>
              <option value="savings">Savings</option>
              <option value="other">Other</option>
            </select>
            <div className="mt-1 text-[10px] text-[#8A918D]">
              How this account is used. Savings usually shouldn&rsquo;t count as everyday spendable cash.
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Balance</label>
            <input type="number" step="0.01" className="input-soft"
              placeholder="0.00"
              value={form.current_source_balance_optional}
              onChange={(e) => setForm({...form, current_source_balance_optional: e.target.value})}
              onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              data-testid={`fs-edit-balance-${source.id}`} />
          </div>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#2C302E] cursor-pointer">
          <input type="checkbox" checked={!!form.contributes_to_total_balance}
            onChange={(e) => setForm({...form, contributes_to_total_balance: e.target.checked})}
            data-testid={`fs-edit-counts-${source.id}`} />
          <span>Include in Real Balance</span>
        </label>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={save} disabled={saving} data-testid={`fs-edit-save-${source.id}`}>
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  const balText = source.current_source_balance_optional != null
    ? formatMoney(source.current_source_balance_optional)
    : "—";

  return (
    <div className="rounded-2xl bg-[#F9F8F6] hover:bg-[#F2EFEB] transition-colors" data-testid={`fs-row-${source.id}`}>
      <div className="flex items-center justify-between gap-3 p-3">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left cursor-pointer"
          title="Click to edit"
        >
          <span className={`p-2 rounded-xl ${meta.affectsCash ? "bg-[#EEF3EC] text-[#3B5B3E]" : "bg-[#FDF2F0] text-[#8C3A30]"}`}>
            <Icon size={16} />
          </span>
          <div className="min-w-0">
            <div className="text-[#2C302E] font-medium truncate">{source.name}</div>
            <div className="text-xs text-[#8A918D]">
              {meta.label}
              {!source.contributes_to_total_balance && <span className="ml-2 text-[#8C5D30]">· not counted as cash</span>}
              {bills.length > 0 && (
                <span className="ml-2">· {bills.length} bill{bills.length === 1 ? "" : "s"} · {formatMoney(totalOutflow)} upcoming</span>
              )}
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="font-heading text-lg font-semibold text-[#2C302E] hover:underline underline-offset-2 decoration-dotted whitespace-nowrap"
          title="Click to edit balance"
          data-testid={`fs-balance-${source.id}`}
        >
          {balText}
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-ghost !p-2"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse bills list" : "Expand bills list"}
            title={bills.length === 0 ? "No bills tied to this account" : (expanded ? "Hide bills" : "Show bills")}
            data-testid={`fs-toggle-bills-${source.id}`}
            disabled={bills.length === 0}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditing(true)} data-testid={`fs-edit-btn-${source.id}`}>Edit</button>
          <button className="btn-ghost !p-2" onClick={onDelete} aria-label="Delete" data-testid={`fs-del-${source.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {expanded && bills.length > 0 && (
        <div className="px-3 pb-3" data-testid={`fs-bills-${source.id}`}>
          <div className="rounded-xl bg-white border border-[#D8CFB9] px-3 py-2 divide-y divide-[#EBE6E0]">
            {bills.map((b) => <BillLine key={b.id} bill={b} />)}
          </div>
        </div>
      )}
    </div>
  );
}
